import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useChatContext } from '../context/ChatContext';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  FileText, 
  MessageSquare, 
  CheckSquare, 
  Settings,
  Plus,
  Edit3,
  Trash2,
  Activity,
  ChevronDown,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import TaskManagement from './TaskManagement';
import ProjectSettings from './ProjectSettings';
import ProjectSettingsInline from './ProjectSettingsInline';
import TimeTracking from './TimeTracking';

const ProjectCommunications = ({ project, client }) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // This would fetch communications/meetings related to this project
    // For now, showing placeholder
    setLoading(false);
  }, [project]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-100 rounded"></div>
            <div className="h-20 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Communications</h2>
          <p className="text-gray-600">
            {client.name} • {project.name}
          </p>
        </div>
        
        <button className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all">
          <Plus className="w-4 h-4" />
          <span>New Communication</span>
        </button>
      </div>

      {/* Communications List */}
      <div className="space-y-4">
        {/* Placeholder for now */}
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No communications yet</h3>
          <p className="text-gray-600 mb-4">
            Communications like emails, meetings, and calls will appear here
          </p>
          <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all">
            Add First Communication
          </button>
        </div>
      </div>
    </div>
  );
};

const ProjectDetail = ({ project, client, onBack, teamMembers = [], user }) => {
  const [activeTab, setActiveTab] = useState('projects');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProjectDetails, setShowProjectDetails] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { setActiveContext } = useChatContext();
  const [editFormData, setEditFormData] = useState({
    name: project?.name || '',
    description: project?.description || '',
    status: project?.status || 'open',
    due_date: project?.due_date ? new Date(project.due_date).toISOString().split('T')[0] : ''
  });

  useEffect(() => {
    if (project?.id) {
      setActiveContext({
        view: 'project',
        client_id: project?.client_id || client?.id || null,
        project_id: project.id
      });
    }
  }, [project?.id, project?.client_id, client?.id, setActiveContext]);

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...editFormData,
        due_date: editFormData.due_date ? new Date(editFormData.due_date).toISOString() : null
      };
      
      await api.put(`/projects/${project.id}`, updateData);
      
      setShowEditModal(false);
      // Refresh project data would happen here
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800',
      'on-hold': 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { id: 'projects', label: 'Projects', icon: CheckSquare },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const handleArchiveProject = async () => {
    const action = project.archived ? 'unarchive' : 'archive';
    if (!window.confirm(`Are you sure you want to ${action} this project?`)) {
      return;
    }
    
    try {
      await api.put(`/projects/${project.id}/archive`, { archived: !project.archived });
      
      // Refresh or go back after archiving
      alert(`Project ${action}d successfully`);
      if (onBack) onBack();
    } catch (error) {
      console.error(`Failed to ${action} project:`, error);
      alert(`Failed to ${action} project`);
    }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/projects/${project.id}`);
      
      alert('Project deleted successfully');
      if (onBack) onBack();
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  return (
    <div className="flex-1 p-4 bg-gray-50 min-h-screen flex flex-col">
      <div className="flex-1 space-y-4 w-full">
        {/* Compact Header */}
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div>
            <h1 className="text-xl font-bold text-gray-900">{client?.name || 'Client'}</h1>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>{project?.name || 'Project'}</span>
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(project?.due_date)}</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project?.status)}`}>
                {project?.status?.charAt(0).toUpperCase() + project?.status?.slice(1) || 'Open'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowProjectDetails(!showProjectDetails)}
            className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span>{showProjectDetails ? 'Hide Details' : 'See More'}</span>
          </button>
          
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit</span>
          </button>

          <button
            onClick={handleArchiveProject}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={project?.archived ? "Unarchive project" : "Archive project"}
          >
            {project?.archived ? (
              <ArchiveRestore className="w-5 h-5 text-blue-600" />
            ) : (
              <Archive className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {!project?.is_general && (
            <button
              onClick={handleDeleteProject}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete project"
            >
              <Trash2 className="w-5 h-5 text-red-600" />
            </button>
          )}
        </div>
      </div>

      {/* Expandable Project Details */}
      {showProjectDetails && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Created</label>
              <div className="flex items-center space-x-2 text-gray-900">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>{formatDate(project?.created_at)}</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Team Members</label>
              <div className="flex items-center space-x-2 text-gray-900">
                <User className="w-4 h-4 text-gray-400" />
                <span>{teamMembers?.length || 0} members</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
              <div className="flex items-center space-x-2 text-gray-900">
                <Activity className="w-4 h-4 text-gray-400" />
                <span>{formatDate(project?.updated_at)}</span>
              </div>
            </div>
          </div>

          {project?.description && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-2">Description</label>
              <p className="text-gray-900">{project.description}</p>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'projects' && (
            <div className="p-6 space-y-6">
              {/* Task Management */}
              <div className="bg-gray-50 rounded-lg p-4">
                <TaskManagement 
                  project={project} 
                  client={client} 
                  teamMembers={teamMembers}
                  isFullWidth={true}
                  user={user}
                />
              </div>

              {/* Time Tracking Section - Collapsed and moved below */}
              <div className="bg-gray-50 rounded-lg">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-gray-600" />
                      <h3 className="font-medium text-gray-900">Time Tracking & Entries</h3>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-500 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-4 pb-4">
                    <TimeTracking projectId={project?.id} />
                  </div>
                </details>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="p-6">
              <div className="text-center py-12">
                <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Activity Timeline</h3>
                <p className="text-gray-600 mb-4">
                  Client communications, project updates, and activity logs will appear here.
                </p>
                <button className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                  View Activity History
                </button>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <ProjectSettingsInline project={project} onUpdate={() => {}} />
          )}
        </div>
      </div>
      </div>

      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Edit Project</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  required
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900"
                >
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on-hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={editFormData.due_date}
                  onChange={(e) => setEditFormData({ ...editFormData, due_date: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 !text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 !text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Project Settings Modal */}
      <ProjectSettings
        project={project}
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onUpdate={() => {
          // Could refresh project data here if needed
          console.log('Project settings updated');
        }}
      />

    </div>
  );
};


// Project Settings Preview Component
const ProjectSettingsPreview = ({ project, onOpenSettings }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (project?.id) {
      loadSettings();
    }
  }, [project?.id]);

  const loadSettings = async () => {
    try {
      const response = await api.get(`/projects/${project.id}/settings`);
      console.log('📊 Settings API Response:', response.data);
      setSettings(response.data);
      setError(null);
    } catch (error) {
      console.error('❌ Failed to load settings:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading project settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  const hasData = settings && (
    (settings.scope_of_work_text && settings.scope_of_work_text.trim().length > 0) ||
    (settings.project_objectives && settings.project_objectives.trim().length > 0) ||
    (settings.key_deliverables && settings.key_deliverables.trim().length > 0) ||
    (settings.timeline_notes && settings.timeline_notes.trim().length > 0) ||
    (settings.stakeholders && settings.stakeholders.trim().length > 0) ||
    (settings.budget_notes && settings.budget_notes.trim().length > 0) ||
    (settings.risk_factors && settings.risk_factors.trim().length > 0) ||
    (settings.success_criteria && settings.success_criteria.trim().length > 0) ||
    (settings.additional_notes && settings.additional_notes.trim().length > 0) ||
    (settings.scope_of_work_files && settings.scope_of_work_files.length > 0)
  );

  console.log('✅ Settings loaded:', settings);
  console.log('📋 Has data:', hasData);
  console.log('🔍 Individual checks:', {
    scope: !!settings?.scope_of_work_text,
    objectives: !!settings?.project_objectives,
    deliverables: !!settings?.key_deliverables,
    timeline: !!settings?.timeline_notes,
    stakeholders: !!settings?.stakeholders,
    budget: !!settings?.budget_notes,
    risk: !!settings?.risk_factors,
    success: !!settings?.success_criteria,
    additional: !!settings?.additional_notes
  });

  return (
    <div className="max-w-5xl mx-auto pb-24">
      {/* Header with Edit Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Project Settings</h2>
          <p className="text-sm text-gray-500 mt-1">View and manage project configuration</p>
        </div>
        <button
          onClick={onOpenSettings}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Edit3 className="w-4 h-4" />
          <span>Edit Settings</span>
        </button>
      </div>

      {!hasData ? (
        // Empty State
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Settings Configured</h3>
          <p className="text-gray-600 mb-4">
            Add project details, scope of work, deliverables, and more.
          </p>
          <button
            onClick={onOpenSettings}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Configure Settings
          </button>
        </div>
      ) : (
        // Settings Preview Cards
        <div className="space-y-6">
          {/* Project Info Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Information</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Project Name</span>
                <p className="text-gray-900 mt-1">{settings.project_name || 'Not specified'}</p>
              </div>
              {settings.project_description && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Description</span>
                  <p className="text-gray-900 mt-1">{settings.project_description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Scope of Work Card */}
          {(settings.scope_of_work_text || (settings.scope_of_work_files && settings.scope_of_work_files.length > 0)) && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <span>Scope of Work</span>
              </h3>
              {settings.scope_of_work_text && (
                <div className="mb-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{settings.scope_of_work_text}</p>
                </div>
              )}
              {settings.scope_of_work_files && settings.scope_of_work_files.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-500 block mb-2">Attached Files</span>
                  <div className="space-y-2">
                    {settings.scope_of_work_files.map((fileUrl, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm text-purple-600">
                        <FileText className="w-4 h-4" />
                        <span>{fileUrl.split('/').pop()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Objectives & Deliverables Card */}
          {(settings.project_objectives || settings.key_deliverables || settings.success_criteria) && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Objectives & Deliverables</h3>
              <div className="space-y-4">
                {settings.project_objectives && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Project Objectives</span>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">{settings.project_objectives}</p>
                  </div>
                )}
                {settings.key_deliverables && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Key Deliverables</span>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">{settings.key_deliverables}</p>
                  </div>
                )}
                {settings.success_criteria && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Success Criteria</span>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">{settings.success_criteria}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline & Stakeholders Card */}
          {(settings.timeline_notes || settings.stakeholders) && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline & Stakeholders</h3>
              <div className="space-y-4">
                {settings.timeline_notes && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Timeline Notes</span>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">{settings.timeline_notes}</p>
                  </div>
                )}
                {settings.stakeholders && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Stakeholders</span>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">{settings.stakeholders}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Budget & Risk Card */}
          {(settings.budget_notes || settings.risk_factors) && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Budget & Risk</h3>
              <div className="space-y-4">
                {settings.budget_notes && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Budget Notes</span>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">{settings.budget_notes}</p>
                  </div>
                )}
                {settings.risk_factors && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Risk Factors</span>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">{settings.risk_factors}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Notes Card */}
          {settings.additional_notes && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{settings.additional_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default ProjectDetail;
